import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { Card, Badge, Spinner, Button } from '../../components/ui';
import { 
  MessageSquare, CheckCircle, XCircle, Wallet, 
  Send, Smartphone, FileText, BookOpen, 
  TrendingUp, Activity, ChevronRight, Zap,
  Users, Building, Shield, Clock, BarChart3,
  DollarSign, UserCheck, AlertCircle, RefreshCw,
  ArrowUpRight, ArrowDownRight, MoreHorizontal,
  Calendar, Filter, Search, Award, Target, 
  Rocket, Headphones, Settings, HelpCircle
} from 'lucide-react';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { request } = useApi();
  const { isSuperAdmin, user } = useAuth();
  const navigate = useNavigate();

  const requestRef = useRef(request);
  requestRef.current = request;

  const userName = localStorage.getItem('wa_name') || 'User';
  const organizationName = localStorage.getItem('wa_org_name') || user?.organizationName || user?.orgName || 'Your Organization';

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (isSuperAdmin) {
          const statsData = await requestRef.current('get', '/api/admin/stats').catch(() => null);
          if (!cancelled) setAdminStats(statsData);
        } else {
          const [statsData, jobsData] = await Promise.all([
            requestRef.current('get', '/api/dashboard/stats').catch(() => null),
            requestRef.current('get', '/api/send/jobs').catch(() => [])
          ]);
          if (!cancelled) {
            setStats(statsData);
            setJobs(jobsData?.slice(0, 5) || []);
          }
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]); 

  // ─ ORG_ADMIN Stat Cards ──
  const orgStatCards = [
    { 
      icon: MessageSquare, 
      label: 'Messages Sent', 
      value: stats?.totalMessagesSent || 0, 
      change: '+23%', 
      changeUp: true,
      color: '#10B981', 
      bg: 'rgba(16,185,129,0.1)' 
    },
    { 
      icon: CheckCircle, 
      label: 'Delivered', 
      value: stats?.totalDelivered || 0, 
      change: '95.5% rate', 
      changeUp: true,
      color: '#3498DB', 
      bg: 'rgba(52,152,219,0.1)' 
    },
    { 
      icon: XCircle, 
      label: 'Failed', 
      value: stats?.totalFailed || 0, 
      change: '-12%', 
      changeUp: false,
      color: '#EF4444', 
      bg: 'rgba(239,68,68,0.1)' 
    },
    { 
      icon: Wallet, 
      label: 'Credits', 
      value: stats?.whatsappStatus?.creditBalance || 0, 
      change: 'remaining', 
      changeUp: true,
      color: '#F59E0B', 
      bg: 'rgba(245,158,11,0.1)' 
    },
  ];

  // ── SUPER_ADMIN Stat Cards ─────
  const superAdminStatCards = [
    { 
      icon: Building, 
      label: 'Total Organizations', 
      value: adminStats?.totalOrganizations || 0,
      color: '#3498DB',
      bg: 'rgba(52,152,219,0.1)',
      change: '+12%',
      changeUp: true
    },
    { 
      icon: Users, 
      label: 'Total Users', 
      value: adminStats?.totalUsers || 0,
      color: '#10B981',
      bg: 'rgba(16,185,129,0.1)',
      change: '+8%',
      changeUp: true
    },
    { 
      icon: DollarSign, 
      label: 'Total Credits', 
      value: adminStats?.totalCredits || 0,
      color: '#F59E0B',
      bg: 'rgba(245,158,11,0.1)',
      change: '+5%',
      changeUp: true
    },
    { 
      icon: Smartphone, 
      label: 'WA Active', 
      value: adminStats?.activeWaAccounts || 0,
      color: '#EF4444',
      bg: 'rgba(239,68,68,0.1)',
      change: '+3%',
      changeUp: true
    },
  ];

  const orgQuickActions = [
    { icon: Send, title: 'Send Campaign', desc: 'Send bulk WhatsApp messages', path: '/send', color: '#25D366' },
    { icon: Smartphone, title: 'WhatsApp Setup', desc: 'Connect your number', path: '/onboarding', color: '#128C7E' },
    { icon: FileText, title: 'Create Template', desc: 'Design message templates', path: '/templates', color: '#3498DB' },
    { icon: BookOpen, title: 'Manage Phonebooks', desc: 'Upload contacts', path: '/phonebooks', color: '#F59E0B' },
  ];

  const superAdminQuickActions = [
    { icon: Building, title: 'Manage Organizations', desc: 'View and approve organizations', path: '/admin/organizations', color: '#3498DB' },
    { icon: Users, title: 'Manage Users', desc: 'View and manage users', path: '/admin/users', color: '#10B981' },
    { icon: Wallet, title: 'Manage Credits', desc: 'Add credits to organizations', path: '/admin/credits', color: '#F59E0B' },
    { icon: BarChart3, title: 'View All Stats', desc: 'System-wide analytics', path: '/admin', color: '#EF4444' },
  ];

  const getStatusBadge = (status) => {
    if (status === 'COMPLETED') return <span className="badge-success">Completed</span>;
    if (status === 'PROCESSING') return <span className="badge-warning">Processing</span>;
    if (status === 'FAILED') return <span className="badge-danger">Failed</span>;
    return <span className="badge-default">Pending</span>;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  // ── SUPER_ADMIN Dashboard ───
  if (isSuperAdmin) {
    return (
      <div className="dashboard">
        {/* Welcome Section - Super Admin */}
        <div className="welcome-section super-admin">
          <div className="welcome-content">
            <div className="welcome-badge">
              <Shield size={14} />
              <span>System Administrator</span>
            </div>
            <h1 className="welcome-title">
              Welcome back, {userName}! 👋
            </h1>
            <p className="welcome-desc">
              Manage organizations, users, and monitor system health on Jipate platform.
            </p>
          </div>
          <div className="welcome-stats">
            <div className="welcome-stat">
              <div className="welcome-stat-value">{adminStats?.totalOrganizations || 0}</div>
              <div className="welcome-stat-label">Total Organizations</div>
            </div>
            <div className="welcome-stat">
              <div className="welcome-stat-value">{adminStats?.totalUsers || 0}</div>
              <div className="welcome-stat-label">Total Users</div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          {superAdminStatCards.map((card, idx) => (
            <div className="stat-card" key={idx}>
              <div className="stat-header">
                <div className="stat-icon" style={{ background: card.bg, color: card.color }}>
                  <card.icon size={20} />
                </div>
                <span className={`stat-change ${card.changeUp ? 'up' : 'down'}`}>
                  {card.changeUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {card.change}
                </span>
              </div>
              <div className="stat-value" style={{ color: card.color }}>{card.value.toLocaleString()}</div>
              <div className="stat-label">{card.label}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions + System Health */}
        <div className="dashboard-two-col">
          <div className="quick-actions-card">
            <div className="card-header">
              <h3 className="card-title">System Management</h3>
              <button className="card-link" onClick={() => navigate('/admin')}>
                View all <ChevronRight size={14} />
              </button>
            </div>
            <div className="quick-actions-list">
              {superAdminQuickActions.map((action, idx) => (
                <div className="quick-action-item" key={idx} onClick={() => navigate(action.path)}>
                  <div className="quick-action-icon" style={{ background: `${action.color}15`, color: action.color }}>
                    <action.icon size={18} />
                  </div>
                  <div className="quick-action-info">
                    <div className="quick-action-title">{action.title}</div>
                    <div className="quick-action-desc">{action.desc}</div>
                  </div>
                  <ChevronRight size={14} className="quick-action-arrow" />
                </div>
              ))}
            </div>
          </div>

          <div className="campaigns-card">
            <div className="card-header">
              <h3 className="card-title">System Health</h3>
              <span className="health-status">
                <Activity size={14} /> Operational
              </span>
            </div>
            <div className="system-health-grid">
              <div className="health-item">
                <div className="health-value" style={{ color: '#3498DB' }}>
                  {adminStats?.activeWaAccounts || 0}
                </div>
                <div className="health-label">Active WA Accounts</div>
              </div>
              <div className="health-item">
                <div className="health-value" style={{ color: '#F59E0B' }}>
                  {adminStats?.pendingOrganizations || 0}
                </div>
                <div className="health-label">Pending Approval</div>
              </div>
              <div className="health-item">
                <div className="health-value" style={{ color: '#10B981' }}>
                  {adminStats?.whatsappOnboarded || 0}
                </div>
                <div className="health-label">WA Onboarded</div>
              </div>
              <div className="health-item">
                <div className="health-value" style={{ color: '#EF4444' }}>
                  {adminStats?.suspendedOrganizations || 0}
                </div>
                <div className="health-label">Suspended</div>
              </div>
            </div>
            <div className="health-last-updated">
              Last updated: {new Date().toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── ORG_ADMIN Dashboard ────────────────────────────────────────────────────
  return (
    <div className="dashboard">
      {/* Welcome Section - Organization Admin */}
      <div className="welcome-section">
        <div className="welcome-content">
          <div className="welcome-badge">
            <Zap size={14} />
            <span>{organizationName}</span>
          </div>
          <h1 className="welcome-title">
            Welcome back, {userName}! 👋
          </h1>
          <p className="welcome-desc">
            Track your performance, manage campaigns, and grow your customer base with Jipate.
          </p>
        </div>
        <div className="welcome-stats">
          <div className="welcome-stat">
            <div className="welcome-stat-value">{stats?.totalMessagesSent?.toLocaleString() || 0}</div>
            <div className="welcome-stat-label">Total Messages</div>
            <div className="welcome-stat-trend up">
              <TrendingUp size={12} /> +18% this month
            </div>
          </div>
          <div className="welcome-stat">
            <div className="welcome-stat-value">{stats?.whatsappStatus?.creditBalance || 0}</div>
            <div className="welcome-stat-label">Credits Left</div>
            <div className="welcome-stat-trend">
              <Activity size={12} /> ~{stats?.whatsappStatus?.creditBalance || 0} messages
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {orgStatCards.map((card, idx) => (
          <div className="stat-card" key={idx}>
            <div className="stat-header">
              <div className="stat-icon" style={{ background: card.bg, color: card.color }}>
                <card.icon size={20} />
              </div>
              <span className={`stat-change ${card.changeUp ? 'up' : 'down'}`}>
                {card.changeUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                {card.change}
              </span>
            </div>
            <div className="stat-value">{card.value.toLocaleString()}</div>
            <div className="stat-label">{card.label}</div>
            <div className="stat-progress">
              <div className="stat-progress-bar" style={{ width: card.value > 0 ? '78%' : '0%' }}></div>
            </div>
          </div>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="dashboard-two-col">
        {/* Quick Actions */}
        <div className="quick-actions-card">
          <div className="card-header">
            <h3 className="card-title">Quick Actions</h3>
            <button className="card-link" onClick={() => navigate('/send')}>
              View all <ChevronRight size={14} />
            </button>
          </div>
          <div className="quick-actions-list">
            {orgQuickActions.map((action, idx) => (
              <div className="quick-action-item" key={idx} onClick={() => navigate(action.path)}>
                <div className="quick-action-icon" style={{ background: `${action.color}15`, color: action.color }}>
                  <action.icon size={18} />
                </div>
                <div className="quick-action-info">
                  <div className="quick-action-title">{action.title}</div>
                  <div className="quick-action-desc">{action.desc}</div>
                </div>
                <ChevronRight size={14} className="quick-action-arrow" />
              </div>
            ))}
          </div>
        </div>

        {/* Recent Campaigns */}
        <div className="campaigns-card">
          <div className="card-header">
            <h3 className="card-title">Recent Campaigns</h3>
            <button className="card-link" onClick={() => navigate('/reports')}>
              View all <ChevronRight size={14} />
            </button>
          </div>
          <div className="table-responsive">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Recipients</th>
                  <th>Sent</th>
                  <th>Failed</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr className="empty-row">
                    <td colSpan={6}>
                      <div className="empty-state">
                        <MessageSquare size={32} className="empty-icon" />
                        <p>No campaigns yet.</p>
                        <button className="empty-link" onClick={() => navigate('/send')}>
                          Send your first campaign →
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  jobs.map(job => (
                    <tr key={job.id}>
                      <td className="campaign-name">{job.name}</td>
                      <td>{job.totalRecipients}</td>
                      <td>{job.sentCount}</td>
                      <td className={job.failedCount > 0 ? 'failed' : ''}>{job.failedCount}</td>
                      <td>{getStatusBadge(job.status)}</td>
                      <td className="campaign-date">
                        <Calendar size={12} className="inline mr-1" />
                        {new Date(job.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;